import React from 'react';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { validatePassword, getPasswordStrengthLabel, getPasswordStrengthColor } from '@/utils/passwordPolicy';

interface PasswordStrengthIndicatorProps {
  password: string;
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  className?: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  personalInfo,
  className = ''
}) => {
  const strength = validatePassword(password, personalInfo);
  const strengthLabel = getPasswordStrengthLabel(strength.score);
  const strengthColor = getPasswordStrengthColor(strength.score);

  if (!password) {
    return null;
  }

  const RequirementItem: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle className="h-4 w-4 text-success" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={met ? 'text-success' : 'text-muted-foreground'}>
        {text}
      </span>
    </div>
  );

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Password Strength Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Password Strength</span>
          <span 
            className="text-sm font-semibold"
            style={{ color: strengthColor }}
          >
            {strengthLabel}
          </span>
        </div>
        <Progress 
          value={strength.score} 
          className="h-2"
          style={{
            '--progress-foreground': strengthColor
          } as React.CSSProperties}
        />
      </div>

      {/* Requirements Checklist */}
      <div className="space-y-1.5">
        <div className="text-sm font-medium text-foreground mb-2">Requirements:</div>
        
        <RequirementItem 
          met={strength.meets.minLength} 
          text="At least 12 characters" 
        />
        
        <RequirementItem 
          met={strength.meets.hasUppercase} 
          text="Include uppercase letter (A-Z)" 
        />
        
        <RequirementItem 
          met={strength.meets.hasLowercase} 
          text="Include lowercase letter (a-z)" 
        />
        
        <RequirementItem 
          met={strength.meets.hasNumbers} 
          text="Include number (0-9)" 
        />
        
        <RequirementItem 
          met={strength.meets.hasSpecialChars} 
          text="Include special character (!@#$%^&*)" 
        />
        
        <RequirementItem 
          met={strength.meets.noCommonPatterns} 
          text="Avoid common passwords" 
        />
        
        <RequirementItem 
          met={strength.meets.noPersonalInfo} 
          text="Don't use personal information" 
        />
      </div>

      {/* Feedback Messages */}
      {strength.feedback.length > 0 && (
        <div className="space-y-1">
          {strength.feedback.map((message, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-warning">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Success Message */}
      {strength.isValid && (
        <div className="flex items-center gap-2 text-sm text-success bg-success/10 p-2 rounded-md">
          <CheckCircle className="h-4 w-4" />
          <span>Password meets all security requirements!</span>
        </div>
      )}
    </div>
  );
};